import React from 'react';
import { DollarSign, CheckCircle2, Bus, Wifi, MapPin, TrendingDown } from 'lucide-react';

const PricingSection = () => {
  const pricingItems = [
    {
      icon: Bus,
      item: "Gestão + App motorista + App condômino",
      price: "R$ 350",
      unit: "/ônibus/mês",
      features: [
        "Gestão completa de passageiros",
        "App do motorista com validação",
        "App do condômino",
        "Suporte técnico incluído"
      ]
    },
    {
      icon: Wifi,
      item: "Validação remota NFC (Bluetooth)",
      price: "R$ 100",
      unit: "/ônibus/mês",
      features: [
        "Tecnologia NFC exclusiva",
        "Validação via Bluetooth",
        "Registro automático",
        "Alertas visuais e por voz"
      ]
    },
    {
      icon: MapPin,
      item: "Georreferenciamento",
      price: "R$ 150",
      unit: "/ônibus/mês",
      features: [
        "Rastreamento em tempo real",
        "Visualização no mapa",
        "Relatórios de trajeto",
        "Cobrado após 30 dias"
      ],
      note: "Cobrado após 30 dias"
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-br from-[#0f1a36] to-[#1a2847] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#007bff] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#008080] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 mb-6">
            <DollarSign className="w-4 h-4 text-[#008080]" />
            <span className="text-sm font-medium text-white">Investimento</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Modelo de Precificação
          </h2>
          <p className="text-xl text-gray-300">
            Investimento transparente e escalável por veículo
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {pricingItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div 
                key={index}
                className="group relative bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:border-[#007bff]/50 hover:bg-white/10 transition-all duration-300 overflow-hidden hover:-translate-y-2"
              >
                {/* Header */}
                <div className="p-8 border-b border-white/10">
                  <div className="w-16 h-16 rounded-2xl bg-[#007bff]/20 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[#007bff]/30 transition-all duration-300">
                    <Icon className="w-8 h-8 text-[#007bff]" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4 leading-tight">
                    {item.item}
                  </h3>
                  
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-white">
                      {item.price}
                    </span>
                    <span className="text-gray-400 text-lg">
                      {item.unit}
                    </span>
                  </div>
                  
                  {item.note && (
                    <div className="mt-4 inline-flex items-center gap-2 bg-[#008080]/20 px-3 py-1.5 rounded-full">
                      <div className="w-2 h-2 rounded-full bg-[#008080]"></div>
                      <span className="text-sm text-[#008080] font-medium">{item.note}</span>
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="p-8">
                  <ul className="space-y-4">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#008080] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total calculation example */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-[#007bff] to-[#008080] rounded-3xl p-10 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-white mb-4">
                  Exemplo: 10 Ônibus
                </h3>
                <div className="space-y-3 text-white/90 text-lg">
                  <div className="flex justify-between items-center">
                    <span>Gestão + Apps:</span>
                    <span className="font-semibold">R$ 3.500/mês</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Validação NFC:</span>
                    <span className="font-semibold">R$ 1.000/mês</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Georreferenciamento:</span>
                    <span className="font-semibold">R$ 1.500/mês</span>
                  </div>
                  <div className="h-px bg-white/20 my-3"></div>
                  <div className="flex justify-between items-center text-2xl font-bold text-white">
                    <span>Total:</span>
                    <span>R$ 6.000/mês</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-start gap-4 mb-6">
                  <TrendingDown className="w-8 h-8 text-white flex-shrink-0" />
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">
                      R$ 600 por ônibus/mês
                    </h4>
                    <p className="text-white/90 leading-relaxed">
                      Investimento completo incluindo todas as funcionalidades e tecnologias
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2 text-white/80 text-sm">
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#008080]" />
                    Sem custos de implantação
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#008080]" />
                    Suporte técnico incluído
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#008080]" />
                    Atualizações automáticas
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#008080]" />
                    Treinamento da equipe
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
